// Lộ trình thăng tiến: danh sách lộ trình, điều kiện, thành tựu, lịch sử đánh giá.
// Chưa nối API: lưu ở localStorage; mọi thao tác đi qua careerApi để sau này thay bằng gọi API.
import { useSyncExternalStore } from 'react';

export type CriterionStatus = 'done' | 'doing' | 'todo';
export interface Criterion { id: string; title: string; required: string; current: string; status: CriterionStatus }
export type AchievementType = 'award' | 'certificate' | 'project';
export interface Achievement { id: string; title: string; date: string; type: AchievementType; description: string }
export interface Evaluation { id: string; date: string; result: 'promoted' | 'continue'; fromRole: string; toRole: string; note: string; by: string }

export interface Roadmap {
  id: string;
  name: string;
  employee: string;
  department: string;
  /** Các bậc trong lộ trình, theo thứ tự: [vị trí đầu, ..., vị trí hiện tại, mục tiêu, các bậc tương lai] */
  steps: string[];
  /** Mốc thời gian / ghi chú cho từng bậc (cùng chỉ số với steps) */
  stepNotes: string[];
  currentIndex: number;
  /** Hạn đạt mục tiêu gần nhất (YYYY-MM) */
  targetMonth?: string;
  criteria: Criterion[];
  achievements: Achievement[];
  evaluations: Evaluation[];
  note?: string;
  attachments: string[];
  createdAt: string;
  updatedAt: string;
}

export const CRITERION_STATUS: Record<CriterionStatus, { label: string; tone: string }> = {
  done: { label: 'Đã đạt', tone: 'green' },
  doing: { label: 'Đang thực hiện', tone: 'rose' },
  todo: { label: 'Chưa đạt', tone: 'gray' },
};

export const DEPARTMENTS = ['Phòng Dịch vụ Chăm sóc', 'Phòng Tư vấn Thẩm mỹ', 'Phòng Kinh doanh', 'Phòng Chăm sóc khách hàng', 'Phòng Marketing', 'Phòng Nhân sự'];

/** Điều kiện gợi ý khi tạo lộ trình (chọn nhanh rồi sửa lại số) */
export const CRITERIA_PRESETS = [
  { key: 'tenure', title: 'Thâm niên công tác', required: 'Tối thiểu 24 tháng' },
  { key: 'retention', title: 'Tỷ lệ khách hàng quay lại', required: 'Từ 70% trong 6 tháng' },
  { key: 'sales', title: 'Doanh số tư vấn (upsell / cross-sell)', required: '100% chỉ tiêu 2 quý liên tiếp' },
  { key: 'cert', title: 'Chứng chỉ quản lý / chuyên môn', required: 'Hoàn thành khoá Quản lý Spa chuyên nghiệp' },
  { key: 'mentor', title: 'Đào tạo kỹ thuật viên mới', required: 'Hướng dẫn ít nhất 2 KTV' },
  { key: 'complaint', title: 'Không có khiếu nại nghiêm trọng', required: '0 khiếu nại trong 12 tháng' },
];

// ---------------------------------------------------------------------------
// Hàm thuần (có test)
// ---------------------------------------------------------------------------
export const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** Tiến độ tới mục tiêu: đạt = 1, đang thực hiện = 0,5 */
export function progressOf(r: Pick<Roadmap, 'criteria'>): number {
  if (!r.criteria.length) return 0;
  const score = r.criteria.reduce((s, c) => s + (c.status === 'done' ? 1 : c.status === 'doing' ? 0.5 : 0), 0);
  return Math.round((score / r.criteria.length) * 100);
}

export const doneCount = (r: Pick<Roadmap, 'criteria'>) => r.criteria.filter(c => c.status === 'done').length;
export const targetRole = (r: Pick<Roadmap, 'steps' | 'currentIndex'>) => r.steps[r.currentIndex + 1] ?? null;
export const currentRole = (r: Pick<Roadmap, 'steps' | 'currentIndex'>) => r.steps[r.currentIndex] ?? '';
export const isCompleted = (r: Pick<Roadmap, 'steps' | 'currentIndex'>) => r.currentIndex >= r.steps.length - 1;
/** Đủ điều kiện xét thăng tiến: còn mục tiêu và mọi điều kiện đã đạt */
export const isReady = (r: Roadmap) => !isCompleted(r) && r.criteria.length > 0 && r.criteria.every(c => c.status === 'done');

export type StepState = 'completed' | 'current' | 'target' | 'future';
export const stepState = (r: Pick<Roadmap, 'currentIndex'>, i: number): StepState =>
  i < r.currentIndex ? 'completed' : i === r.currentIndex ? 'current' : i === r.currentIndex + 1 ? 'target' : 'future';

export type RoadmapFilter = 'all' | 'active' | 'ready' | 'completed';
export function filterRoadmaps(list: Roadmap[], filter: RoadmapFilter, q: string): Roadmap[] {
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();
  const k = norm(q.trim());
  return list
    .filter(r => filter === 'all' || (filter === 'completed' ? isCompleted(r) : filter === 'ready' ? isReady(r) : !isCompleted(r)))
    .filter(r => !k || norm([r.name, r.employee, r.department, ...r.steps].join(' ')).includes(k))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Kết quả đánh giá: thăng tiến thì lên bậc kế tiếp và đặt lại điều kiện cho mục tiêu mới */
export function applyEvaluation(r: Roadmap, result: Evaluation['result'], note: string, by: string, now = new Date().toISOString()): Roadmap {
  const ev: Evaluation = { id: uid(), date: now, result, fromRole: currentRole(r), toRole: targetRole(r) ?? currentRole(r), note: note.trim(), by };
  if (result === 'continue' || isCompleted(r)) return { ...r, evaluations: [ev, ...r.evaluations], updatedAt: now };
  const stepNotes = [...r.stepNotes];
  stepNotes[r.currentIndex + 1] = `Đạt ${now.slice(5, 7)}/${now.slice(0, 4)}`;
  return {
    ...r,
    currentIndex: r.currentIndex + 1,
    stepNotes,
    criteria: r.criteria.map(c => ({ ...c, status: 'todo' as const, current: '' })),
    targetMonth: undefined,
    evaluations: [ev, ...r.evaluations],
    updatedAt: now,
  };
}

// ---------------------------------------------------------------------------
// Dữ liệu mẫu
// ---------------------------------------------------------------------------
const SEED: Roadmap[] = [
  {
    id: 'seed-spa', name: 'KTV Trưởng lên Quản lý Spa', employee: 'Trần Thị B', department: 'Phòng Dịch vụ Chăm sóc',
    steps: ['Kỹ thuật viên', 'KTV Trưởng', 'Quản lý Spa', 'Quản lý Chi nhánh'], stepNotes: ['Tháng 3/2024', 'Từ 02/2026', '', 'Dự kiến 2028'],
    currentIndex: 1, targetMonth: '2026-12',
    criteria: [
      { id: 'c1', title: 'Thâm niên công tác', required: 'Tối thiểu 24 tháng', current: '26 tháng', status: 'done' },
      { id: 'c2', title: 'Tỷ lệ khách hàng quay lại', required: 'Từ 70% trong 6 tháng', current: '75%', status: 'done' },
      { id: 'c3', title: 'Doanh số tư vấn (upsell / cross-sell)', required: '100% chỉ tiêu 2 quý liên tiếp', current: '85% chỉ tiêu quý', status: 'doing' },
      { id: 'c4', title: 'Chứng chỉ quản lý Spa chuyên nghiệp', required: 'Hoàn thành chứng chỉ', current: 'Đang học (dự kiến xong 11/2026)', status: 'todo' },
      { id: 'c5', title: 'Đào tạo kỹ thuật viên mới', required: 'Hướng dẫn ít nhất 2 KTV', current: 'Đã hướng dẫn 3 KTV', status: 'done' },
    ],
    achievements: [
      { id: 'a1', title: 'Kỹ thuật viên xuất sắc năm 2025', date: '2026-01', type: 'award', description: 'Điểm hài lòng của khách cao nhất (4,9/5).' },
      { id: 'a2', title: 'Chứng chỉ Chăm sóc da chuyên sâu', date: '2025-06', type: 'certificate', description: 'Hoàn thành khoá công nghệ Laser và phục hồi da.' },
      { id: 'a3', title: 'Best Seller tháng 8', date: '2026-08', type: 'project', description: 'Vượt 150% chỉ tiêu tư vấn liệu trình cao cấp.' },
    ],
    evaluations: [], attachments: [], createdAt: '2026-02-01T09:00:00+07:00', updatedAt: '2026-09-20T09:00:00+07:00',
  },
  {
    id: 'seed-sale', name: 'Tư vấn viên lên Trưởng nhóm Tư vấn', employee: 'Lê Ngọc Bảo Trâm', department: 'Phòng Tư vấn Thẩm mỹ',
    steps: ['Tư vấn viên', 'Trưởng nhóm Tư vấn', 'Quản lý Tư vấn'], stepNotes: ['Từ 06/2025', '', ''],
    currentIndex: 0, targetMonth: '2026-10',
    criteria: [
      { id: 'd1', title: 'Thâm niên công tác', required: 'Tối thiểu 12 tháng', current: '16 tháng', status: 'done' },
      { id: 'd2', title: 'Tỷ lệ chốt khách tại cơ sở', required: 'Từ 45% trong 2 quý', current: '52%', status: 'done' },
      { id: 'd3', title: 'Không có khiếu nại nghiêm trọng', required: '0 khiếu nại trong 12 tháng', current: '0 khiếu nại', status: 'done' },
    ],
    achievements: [{ id: 'b1', title: 'Top doanh số quý 2/2026', date: '2026-07', type: 'project', description: 'Doanh số cao nhất chi nhánh Thảo Điền.' }],
    evaluations: [], attachments: [], createdAt: '2026-06-01T09:00:00+07:00', updatedAt: '2026-10-01T09:00:00+07:00',
  },
];

// ---------------------------------------------------------------------------
// Kho dữ liệu
// ---------------------------------------------------------------------------
const KEY = 'career_roadmaps_v1';
let state: Roadmap[] | null = null;
const listeners = new Set<() => void>();

function load(): Roadmap[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) return v; }
  } catch { /* dữ liệu hỏng → dùng mẫu */ }
  return SEED;
}
const get = () => (state ??= load());
function commit(next: Roadmap[]) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { throw new Error('Bộ nhớ trình duyệt đã đầy, không lưu được lộ trình.'); }
  state = next;
  listeners.forEach(l => l());
}
if (typeof window !== 'undefined') window.addEventListener('storage', e => { if (e.key === KEY) { state = load(); listeners.forEach(l => l()); } });

export function useRoadmaps() {
  return useSyncExternalStore(l => { listeners.add(l); return () => { listeners.delete(l); }; }, get);
}

const update = (id: string, fn: (r: Roadmap) => Roadmap) =>
  commit(get().map(r => (r.id === id ? { ...fn(r), updatedAt: new Date().toISOString() } : r)));

export type RoadmapInput = Pick<Roadmap, 'name' | 'employee' | 'department' | 'steps' | 'currentIndex' | 'targetMonth' | 'criteria' | 'note' | 'attachments'>;

export const careerApi = {
  create(input: RoadmapInput): Roadmap {
    const now = new Date().toISOString();
    const r: Roadmap = { ...input, id: uid(), stepNotes: input.steps.map(() => ''), achievements: [], evaluations: [], createdAt: now, updatedAt: now };
    commit([r, ...get()]);
    return r;
  },
  update(id: string, input: RoadmapInput) {
    update(id, r => ({ ...r, ...input, stepNotes: input.steps.map((_, i) => r.stepNotes[i] ?? '') }));
  },
  remove(id: string) { commit(get().filter(r => r.id !== id)); },
  setCriterion(id: string, criterionId: string, patch: Partial<Criterion>) {
    update(id, r => ({ ...r, criteria: r.criteria.map(c => (c.id === criterionId ? { ...c, ...patch } : c)) }));
  },
  addAchievement(id: string, a: Omit<Achievement, 'id'>) { update(id, r => ({ ...r, achievements: [{ ...a, id: uid() }, ...r.achievements] })); },
  removeAchievement(id: string, aid: string) { update(id, r => ({ ...r, achievements: r.achievements.filter(a => a.id !== aid) })); },
  evaluate(id: string, result: Evaluation['result'], note: string, by: string) {
    const r = get().find(x => x.id === id);
    if (r) commit(get().map(x => (x.id === id ? applyEvaluation(r, result, note, by) : x)));
  },
};
